<template>
  <div class="create-group-modal">
    <b-button v-required-role="'ROLE_BACKOFFICE_GROUP_POST'" @click="openModal" size="sm" variant="success">
      {{ $t('btn.add') }}
    </b-button>
    <b-modal
        ref="create-security-group-modal"
        size="xl"
        :hide-footer="isLoading"
    >
      <div v-if="isLoading" class="d-flex justify-content-center m-5">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">{{$t('state.loading')}}</span>
        </div>
      </div>
      <template v-if="!isLoading" #modal-header>
        <div class="w-100 d-flex justify-content-between align-items-center">
          <h4>{{ $t('groups.add_new_group.title') }}</h4>
          <div class="d-flex">
            <b-button type="button" class="mr-1" variant="secondary" @click="hideModal">{{
                $t('btn.cancel')
              }}
            </b-button>
            <b-button variant="primary" @click="saveGroup">
              {{ !groupId ? $t('btn.add') : $t('btn.edit')}}
            </b-button>
          </div>
        </div>
      </template>
      <b-card v-if="!isLoading">
        <b-card-body>
          <b-form-group
              v-required-role="'ROLE_BACKOFFICE_GROUP_POST'"
              :label="$t('groups.add_new_group.group_name')"
              label-for="group-name"
          >
            <b-form-input
                v-model="groupName"
                @input="$v.groupName.$model = $event"
                :state="!$v.groupName.$dirty ? null :(!$v.groupName.$error)"
                trim></b-form-input>
            <p v-if="$v.groupName.$dirty && !$v.groupName.required" class="text-danger">{{
                $t('groups.errors.required')
              }}</p>
          </b-form-group>
          <b-form-group
              v-required-role="'ROLE_BACKOFFICE_GROUP_POST'"
              :label="$t('groups.add_new_group.group_key')"
              label-for="group-key"
          >
            <b-form-input
                id="group-name"
                @input="$v.groupKey.$model = $event"
                :state="!$v.groupKey.$dirty ? null :(!$v.groupKey.$error)"
                v-model="groupKey"
                :readonly="groupId ? true : false"
                trim></b-form-input>
            <p v-if="$v.groupKey.$dirty && !$v.groupKey.required" class="text-danger">{{
                $t('groups.errors.required')
              }}</p>
          </b-form-group>
          <div class="col-12">
            <div class="row">
              <div v-required-role="'ROLE_BACKOFFICE_GROUP_ROLE_GET'" id="availableRoles" class="col-5">
                <b-card
                    border-variant="primary"
                    header-bg-variant="success"
                    header-text-variant="white"
                    align="left"
                >
                  <template #header>
                    <div class="d-flex align-items-center justify-content-between">
                      <p class="mb-0">{{ availableRolesHeader }}</p>
                      <button class="btn btn-primary btn-xs"
                              @click="toggleAll('availableRoles')">{{ $t('groups.add_new_group.toggle_all') }}
                      </button>
                    </div>
                  </template>
                  <div class="d-flex">
                    <b-form-group
                        label-for="group-key"
                        class="mr-1"
                    >
                      <b-form-input id="group-nam1e" :placeholder="$t('groups.add_new_group.find_role')"
                                    v-model="filterGroupsPhrase" trim></b-form-input>
                    </b-form-group>
                    <select v-model="filterCategorySelect" class="form-control">
                      <option>{{ $t('groups.add_new_group.all') }}</option>
                      <option :value="category" v-for="(category, index) in roleCategories" :key="index">{{
                          category
                        }}
                      </option>
                    </select>
                  </div>
                  <draggable v-required-role="'ROLE_BACKOFFICE_GROUP_ROLE_GET'" class="create-group-modal__groups-list"
                             :list="availableRoles" group="people">
                    <div
                        v-for="role in filteredGroups"
                        :key="role.role"
                    >
                      <b-form-checkbox
                          :id="role.role"
                          :name="role.role"
                          :value="role.role"
                      >
                        <div class="d-flex create-group-modal__role-text" :title="role.role">
                          <div v-if="role.category"
                               class="create-group-modal__role-text text-success font-weight-bold mr-1">{{
                              role.category
                            }}
                          </div>
                          <div class="font-weight-normal">{{ role.role }}</div>
                        </div>
                      </b-form-checkbox>
                    </div>
                  </draggable>
                </b-card>
              </div>
              <div class="col-2 d-flex flex-column justify-content-center align-items-center">
                <b-button @click="moveToSelectedRoles" class="mb-2 create-group-modal__operation-button"
                          variant="success">
                  {{ $t('groups.add_new_group.add') }}
                  <i class="fas fa-arrow-right ml-1"></i>
                </b-button>
                <b-button @click="moveToAvailableRoles" class="create-group-modal__operation-button" variant="danger">
                  <i class="fas fa-arrow-left mr-1"></i>
                  {{ $t('groups.add_new_group.remove') }}
                </b-button>
              </div>
              <div v-required-role="'ROLE_BACKOFFICE_GROUP_ROLE_POST'" id="selectedRoles" class="col-5">
                <b-card
                    border-variant="primary"
                    header-bg-variant="success"
                    header-text-variant="white"
                    align="left"
                >
                  <template #header>
                    <div class="d-flex align-items-center justify-content-between">
                      <p class="mb-0">{{ selectedRolesHeader }}</p>
                      <button class="btn btn-primary btn-xs" @click="toggleAll('selectedRoles')">
                        {{ $t('groups.add_new_group.toggle_all') }}
                      </button>
                    </div>
                  </template>
                  <div class="d-flex">
                    <b-form-group
                        label-for="group-key"
                        class="mr-1"
                    >
                      <b-form-input id="group-nam1e" :placeholder="$t('groups.add_new_group.find_role')"
                                    v-model="filterSelectedGroupsPhrase" trim></b-form-input>
                    </b-form-group>
                    <select v-model="filterSelectedCategorySelect" class="form-control">
                      <option>{{ $t('groups.add_new_group.all') }}</option>
                      <option :value="category" v-for="(category, index) in roleCategories" :key="index">{{
                          category
                        }}
                      </option>
                    </select>
                  </div>
                  <draggable class="create-group-modal__groups-list" :list="filteredSelectedGroups" group="people">
                    <div
                        v-for="role in filteredSelectedGroups"
                        :key="role.role"
                    >
                      <b-form-checkbox
                          :id="role.role"
                          :name="role.role"
                          :value="role.role"
                      >
                        <div class="d-flex create-group-modal__role-text" :title="role.role">
                          <div v-if="role.category"
                               class="create-group-modal__role-text text-success font-weight-bold mr-1">{{
                              role.category
                            }}
                          </div>
                          <div class="font-weight-normal">{{ role.role }}</div>
                        </div>
                      </b-form-checkbox>
                    </div>
                  </draggable>
                </b-card>
              </div>
            </div>
          </div>
        </b-card-body>
      </b-card>
      <template v-if="!isLoading" #modal-footer>
        <div class="w-100 d-flex justify-content-end">
          <b-button type="button" class="mr-1" variant="secondary" @click="hideModal">{{
              $t('groups.button.cancel')
            }}
          </b-button>
          <b-button variant="primary" @click="saveGroup">
            {{ !groupId ? $t('groups.button.add') : $t('groups.button.edit') }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import axios from "axios";
import {required} from 'vuelidate/lib/validators';
import alertify from 'alertifyjs';

export default {
  components: {draggable},
  data() {
    return {
      isLoading: true,
      status: '',
      groupName: '',
      groupKey: '',
      groupId: null,
      filterGroupsPhrase: '',
      filterCategorySelect: '',
      filterSelectedGroupsPhrase: '',
      filterSelectedCategorySelect: '',
      availableRoles: [],
      selectedRoles: [],
      availableRolesChecked: [],
      selectedRolesChecked: [],
      roleCategories: [],
      noCategoriesFilter: this.$t('groups.add_new_group.no_categories'),
      allCategoriesFilter: this.$t('groups.add_new_group.all'),
      errors: {
        required: this.$t('validate.required_field')
      }
    }
  },
  validations: {
    groupName: {
      required
    },
    groupKey: {
      required
    }
  },
  computed: {
    filteredGroups() {
      let allRoles = this.availableRoles;

      if (allRoles.length > 0 && this.filterCategorySelect !== '' && this.filterCategorySelect !== this.allCategoriesFilter) {
        allRoles = allRoles.filter(role => {
          if (role.category) {
            return role.category.toLowerCase().match(this.filterCategorySelect.toLowerCase());
          } else if (this.filterCategorySelect === this.noCategoriesFilter) {
            return role;
          }
        });
      }

      const filteredRoles = allRoles.filter(role => role.role.toLowerCase().match(this.filterGroupsPhrase.toLowerCase()));
      const sortedRoles = this.sortRoles(filteredRoles);

      return sortedRoles;
    },
    filteredSelectedGroups() {
      let allRoles = this.selectedRoles;

      if (allRoles.length > 0 && this.filterSelectedCategorySelect !== '' && this.filterSelectedCategorySelect !== this.allCategoriesFilter) {
        allRoles = allRoles.filter(role => {
          if (role.category) {
            return role.category.toLowerCase().match(this.filterSelectedCategorySelect.toLowerCase());
          } else if (this.filterSelectedCategorySelect === this.noCategoriesFilter) {
            return role;
          }
        });
      }

      const filteredRoles = allRoles.filter(role => role.role.toLowerCase().match(this.filterSelectedGroupsPhrase.toLowerCase()));
      const sortedRoles = this.sortRoles(filteredRoles);

      return sortedRoles;
    },
    availableRolesHeader() {
      const header = `${this.$t('groups.add_new_group.available_roles')} (${this.availableRoles.length})`;
      return header;
    },
    selectedRolesHeader() {
      const header = `${this.$t('groups.add_new_group.selected_roles')} (${this.selectedRoles.length})`;
      return header;
    }
  },
  methods: {
    sortRoles(array) {
      function compare(a, b) {
        if (a.category === b.category) {
          return 0;
        } else if (!a.category) {
          return 1;
        } else if (!b.category) {
          return -1;
        } else {
          return a.category > b.category ? 1 : -1;
        }
      }

      return array.sort(compare);
    },
    getAvailableRole(item) {
      axios.get('/api/role?limit=300')
          .then(response => {
            this.availableRoles = response.data.items;
            this.categories = this.prepareCategoryList(response.data.items);

            if (item && item.id) {
              this.setEditData(item);
            } else {
              this.isLoading = false;
            }
          }).catch(error => {
        console.log(error);
      });
    },
    saveGroup() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        const httpMethod = this.groupId ? 'put' : 'post';

        const dataToSend = {
          id: this.groupId,
          name: this.groupName,
          key: this.groupKey
        };

        axios[httpMethod](`/api/group`, dataToSend)
            .then(response => {
              this.saveRole(response.data.id);
            })
            .catch(error => {
              alertify.error(error.response.data.label);
            });
      }
    },
    saveRole(groupId) {
      const rolesToSend = this.selectedRoles.map(role => {
        return {role: role.role};
      });

      const data = {
        roles: rolesToSend
      }

      axios.post(`/api/group/${groupId}/role`, data)
          .then(() => {
            this.hideModal();
            this.$emit('savedRoles')
          })
          .catch(error => {
            alertify.error(error.response.data.label);
          });
    },
    setEditData(item) {
      if (item) {
        this.groupId = item.id;

        axios.get(`/api/group/${item.id}`)
            .then(response => {
              const roles = [];

              this.availableRoles.forEach(role => {
                response.data.item.roles.forEach(roleFromResponse => {
                  if (role.role === roleFromResponse.role) {
                    roles.push(role);
                  }
                })
              });

              this.availableRoles = this.availableRoles.filter(item => !roles.includes(item));

              this.groupName = response.data.item.name;
              this.groupKey = response.data.item.key;
              this.selectedRoles = roles;
              this.isLoading = false;
            })
            .catch(error => {
              console.log(error);
            });
      }
    },
    moveToSelectedRoles() {
      const checkedCheckboxes = document.querySelectorAll('#availableRoles input:checked');
      checkedCheckboxes.forEach(checkbox => {
        const findCheckedRoles = this.availableRoles.find(role => role.role === checkbox.value);

        this.selectedRoles.push(findCheckedRoles);
        this.availableRoles = this.availableRoles.filter(role => role.role !== checkbox.value);
      });
    },
    moveToAvailableRoles() {
      const checkedCheckboxes = document.querySelectorAll('#selectedRoles input:checked');
      checkedCheckboxes.forEach(checkbox => {
        const findCheckedRoles = this.selectedRoles.find(role => role.role === checkbox.value);

        this.availableRoles.unshift(findCheckedRoles);
        this.selectedRoles = this.selectedRoles.filter(role => role.role !== checkbox.value);
      });
    },
    toggleAll(id) {
      const checkboxes = document.querySelectorAll(`#${id} input`);

      checkboxes.forEach(checkbox => {
        checkbox.checked = !checkbox.checked;
      });
    },
    prepareCategoryList() {
      const noCategoriesLabel = this.$t('groups.add_new_group.no_categories');

      this.$store.dispatch('administrator/getRoleCategories')
          .then(data => {
            this.roleCategories = [noCategoriesLabel, ...data]
          })
    },
    openModal(item) {
      this.resetProperties();
      this.getAvailableRole(item);
      this.$refs['create-security-group-modal'].show();
    },
    hideModal() {
      this.$refs['create-security-group-modal'].hide();
    },
    resetProperties() {
      this.groupId = null;
      this.status = '';
      this.groupName = '';
      this.groupKey = '';
      this.filterGroupsPhrase = '';
      this.filterCategorySelect = '';
      this.filterSelectedGroupsPhrase = '';
      this.filterSelectedCategorySelect = '';
      this.availableRoles = [];
      this.selectedRoles = [];
      this.availableRolesChecked = [];
      this.selectedRolesChecked = [];
      this.isLoading = true;

      this.$v.$reset();
    }
  }
}
</script>

<style>

</style>
